import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { IoMdArrowBack } from "react-icons/io";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../../src/Config.js";
import Pagination from "../../../src/Component/Pagination";
import "../../Component/sections/allSection.css";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

function BreakdownListView({ isSidebarExpanded }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const listType = searchParams.get("list_type") || "total";
  const departmentId = searchParams.get("department_id") || "";
  const filterOption = searchParams.get("filter_option") || "all";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [departmentName, setDepartmentName] = useState("");

  const containerRef = useRef(null);

  // const formatDate = (date) => {
  //   if (!date) return "NA";
  //   try {
  //     const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  //     return new Date(date)
  //       .toLocaleDateString("en-CA", options)
  //       .replace(/-/g, "/");
  //   } catch (error) {
  //     return "NA";
  //   }
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";

    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "Invalid date";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}/${get("month")}/${get("year")}`;
  };

  const fetchData = async (
    currentPage,
    currentItemsPerPage,
    searchQuery = "",
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      if (!authKey) {
        console.error("No authentication key found");
        setLoading(false);
        return;
      }

      const params = {
        filter_option: filterOption || "All",
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (departmentId && departmentId.trim() !== "") {
        params.department_id = departmentId;
      }

      if (
        startDate &&
        endDate &&
        startDate.trim() !== "" &&
        endDate.trim() !== ""
      ) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.query = searchQuery.trim();
      }

      console.log("Fetching Breakdown list with params:", params);

      const response = await axios.get(
        `${API_URL}/api/kpi/breakdown-maintenance/total-list`,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
          params,
        },
      );

      console.log("API Response:", response.data);

      setReports(response.data.items || []);
      setTotalItems(response.data.total_items || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching Breakdown list:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      setLoading(false);
      setReports([]);
      setTotalItems(0);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      fetchData(1, itemsPerPage, searchQuery);
    }, 300),
    [itemsPerPage, listType, departmentId, filterOption, startDate, endDate],
  );

  useEffect(() => {
    fetchData(page, itemsPerPage, searchInput);
  }, [
    page,
    itemsPerPage,
    listType,
    departmentId,
    filterOption,
    startDate,
    endDate,
  ]);

  const handlePaginationChange = (currentPage, newItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(newItemsPerPage);
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      setPage(1);
      fetchData(1, itemsPerPage, "");
    } else {
      debouncedSearch(value);
    }
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const closePopups = () => {
    setIsSortPopup(false);
  };

  const handleRefresh = () => {
    setSearchInput("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "#2980B9";
      case "cancelled":
        return "#F81700";
      case "hold":
        return "#D4AC0D";
      case "not in tenure":
        return "#313639";
      case "pending":
        return "#C0612B";
      case "in progress":
        return "#df7f26";
      case "completed":
        return "#2E8B57";
      default:
        return "black";
    }
  };

  const getScheduleStatusColor = (scheduleStatus) => {
    switch (scheduleStatus?.toLowerCase()) {
      case "open":
        return "#2980B9";
      case "closed":
        return "#2E8B57";
      case "in progress":
        return "#df7f26";
      default:
        return "#6b7280";
    }
  };

  const getListTypeDisplay = () => {
    switch (listType) {
      case "total":
        return "Total Breakdowns";
      case "this_month":
        return "This Month";
      default:
        return "Breakdowns";
    }
  };

  const formatDates = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return formatter.format(date);
    } catch (error) {
      return "";
    }
  };

  return (
    <div
      className={`data-list-content-container page-content ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="data-list-content" ref={containerRef}>
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side">
              {/* <Tooltip title="Back" arrow>
                <IoMdArrowBack className="back-company" onClick={handleBack} />
              </Tooltip> */}
              <span className="report-name">
                Breakdown Maintenance - {getListTypeDisplay()}
              </span>

              {departmentName && (
                <span
                  className="department-show"
                  style={{
                    color: "green",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {departmentName}
                </span>
              )}

              {startDate && endDate && (
                <span
                  className="option-show"
                  style={{
                    color: "#ce9139ed",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {formatDates(startDate)} - {formatDates(endDate)}
                </span>
              )}
            </div>

            <div className="data-list-heading-content-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="search-bar"
                />
                <IoSearchOutline className="search-bar-icon" />
              </div>

              <Tooltip title="Refresh List" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>

              <button className="sort-button" onClick={handleSortpopup}>
                <PiDotsThreeOutlineVerticalFill className="sort-icon" />
              </button>

              {isSortPopup && (
                <div className="sort-popup-container">
                  <div className="sort-popup-content">
                    <div className="sort-by">SORT BY</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body" onClick={closePopups}>
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : reports.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>
                    <tr>
                      <th className="center-alignment">SR. NO.</th>
                      <th>DEPARTMENT</th>
                      <th>TAG NO</th>
                      <th>DATE</th>
                      <th className="">STATUS</th>
                      {/* <th className="center-alignment">SCHEDULE STATUS</th> */}
                      <th>PROBLEM DESCRIPTION</th>
                      <th>PERMIT NO</th>
                    </tr>
                  </thead>
                  <tbody className="data-list-scroll-bodys">
                    {reports.map((report, index) => (
                      <tr key={report.id} className="hoverable-row">
                        <td className="center-alignment">
                          {(page - 1) * itemsPerPage + index + 1}
                        </td>
                        <td>{report.department_name || "NA"}</td>
                        <td>{report.tag_no || "NA"}</td>
                        <td>{formatDate(report.date)}</td>
                        <td className="new-status-schedules">
                          <div
                            style={{
                              padding: "5px 1px",
                              borderRadius: "30px",
                              textAlign: "center",
                              color: "white",
                              backgroundColor: getStatusColor(report.status),
                              width: "6vw",
                            }}
                          >
                            {report.status
                              ? report.status.charAt(0).toUpperCase() +
                                report.status.slice(1).toLowerCase()
                              : "NA"}
                          </div>
                        </td>
                        {/* <td className="status-schedules">
                          <div
                            style={{
                              backgroundColor: getScheduleStatusColor(report.schedule_status),
                              width: "6vw",
                            }}
                          >
                            {report.schedule_status
                              ? report.schedule_status.charAt(0).toUpperCase() +
                                report.schedule_status.slice(1).toLowerCase()
                              : "NA"}
                          </div>
                        </td> */}
                        <td>{report.problem_desc || "NA"}</td>
                        <td>{report.permit_no || "NA"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">
                    No {getListTypeDisplay()} Records Found
                  </div>
                </div>
              )}
            </div>

            {totalItems > 0 && (
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BreakdownListView;
